import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useFieldConfig } from '@/contexts/FieldConfigContext';
import type { FieldConfig, DurationUnit, TimelineSettings } from '@/contexts/FieldConfigContext';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ChevronDown, ArrowLeft } from 'lucide-react';

interface ProjectCreationSetupProps {
  onNavigate?: (page: string) => void;
}

const SECTION_LABELS: Record<FieldConfig['section'], string> = {
  essential: 'Essential Fields',
  general: 'General Information',
  timeline: 'Timeline & Scheduling',
  management: 'Management & Resources',
};

const SECTION_DESCRIPTIONS: Record<FieldConfig['section'], string | undefined> = {
  essential: 'These fields are always required',
  general: undefined,
  timeline: undefined,
  management: undefined,
};

export function ProjectCreationSetup({ onNavigate }: ProjectCreationSetupProps = {}) {
  const { fieldConfigs, updateFieldConfig, timelineSettings, updateTimelineSettings } = useFieldConfig();
  const [localConfigs, setLocalConfigs] = useState(fieldConfigs);
  const [localTimelineSettings, setLocalTimelineSettings] = useState(timelineSettings);
  const [hasChanges, setHasChanges] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    essential: true,
    general: true,
    timeline: true,
    management: true,
  });

  useEffect(() => {
    setLocalConfigs(fieldConfigs);
  }, [fieldConfigs]);

  useEffect(() => {
    setLocalTimelineSettings(timelineSettings);
  }, [timelineSettings]);

  useEffect(() => {
    const configsDifferent = JSON.stringify(localConfigs) !== JSON.stringify(fieldConfigs);
    const timelineSettingsDifferent = JSON.stringify(localTimelineSettings) !== JSON.stringify(timelineSettings);
    setHasChanges(configsDifferent || timelineSettingsDifferent);
  }, [localConfigs, fieldConfigs, localTimelineSettings, timelineSettings]);

  const handleToggle = (key: string, checked: boolean) => {
    setLocalConfigs(prev =>
      prev.map(config => {
        // Don't allow toggling always mandatory fields
        if (config.key === key && !config.isAlwaysMandatory) {
          return { ...config, isMandatory: checked };
        }
        return config;
      })
    );
  };

  const handleSave = () => {
    localConfigs.forEach(config => {
      updateFieldConfig(config.key, { 
        isMandatory: config.isMandatory,
        autoGenerateCode: config.autoGenerateCode 
      });
    });
    updateTimelineSettings(localTimelineSettings);
    setHasChanges(false);
  };

  const handleBack = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        onNavigate?.('settings');
      }
    } else {
      onNavigate?.('settings');
    }
  };

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const groupedFields = localConfigs.reduce((acc, field) => {
    if (!acc[field.section]) {
      acc[field.section] = [];
    }
    acc[field.section].push(field);
    return acc;
  }, {} as Record<FieldConfig['section'], FieldConfig[]>);

  const getMandatoryCount = (section: FieldConfig['section']) => {
    const fields = groupedFields[section] || [];
    const mandatoryCount = fields.filter(f => f.isMandatory).length;
    const totalCount = fields.length;
    return { mandatoryCount, totalCount };
  };

  return (
    <TooltipProvider>
      <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-background">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <p className="text-sm text-muted-foreground">
                Configure mandatory fields for new projects
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-3xl mx-auto p-6 space-y-6 pb-32">
          {/* Sort sections to show essential first */}
          {(['essential', 'general', 'timeline', 'management'] as FieldConfig['section'][])
            .filter(section => groupedFields[section])
            .map(section => (
            <Collapsible
              key={section}
              open={openSections[section]}
              onOpenChange={() => toggleSection(section)}
            >
              <div className="border rounded-lg">
                <CollapsibleTrigger className="w-full">
                  <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h2 className="text-lg font-semibold">{SECTION_LABELS[section]}</h2>
                        {section === 'essential' && SECTION_DESCRIPTIONS[section] && (
                          <span className="text-sm text-muted-foreground font-normal">
                            ({SECTION_DESCRIPTIONS[section]})
                          </span>
                        )}
                        {!openSections[section] && section !== 'essential' && (
                          <span className="text-xs text-muted-foreground font-normal">
                            - {getMandatoryCount(section).mandatoryCount}/{getMandatoryCount(section).totalCount} required
                          </span>
                        )}
                      </div>
                      {section !== 'essential' && SECTION_DESCRIPTIONS[section] && (
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {SECTION_DESCRIPTIONS[section]}
                        </p>
                      )}
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 transition-transform ${
                        openSections[section] ? 'transform rotate-180' : ''
                      }`}
                    />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="border-t">
                    {/* Timeline Settings - only show in timeline section */}
                    {section === 'timeline' && (
                      <div className="p-4 bg-muted/30 border-b space-y-6">
                        <div>
                          <h3 className="text-sm font-semibold mb-1">Timeline Settings</h3>
                          <p className="text-xs text-muted-foreground">
                            Configure default behavior for timeline fields
                          </p>
                        </div>

                        {/* Default Duration Unit and Default Mode - Side by Side */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="default-duration-unit" className="text-sm font-medium">
                              Default Duration Unit
                            </Label>
                            <Select
                              value={localTimelineSettings.defaultDurationUnit}
                              onValueChange={(value: DurationUnit) => 
                                setLocalTimelineSettings(prev => ({ ...prev, defaultDurationUnit: value }))
                              }
                            >
                              <SelectTrigger id="default-duration-unit" className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="days">Days</SelectItem>
                                <SelectItem value="weeks">Weeks</SelectItem>
                                <SelectItem value="months">Months</SelectItem>
                                <SelectItem value="years">Years</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                              The default unit shown when creating a new project
                            </p>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="default-mode" className="text-sm font-medium">
                              Default Mode
                            </Label>
                            <Select
                              value={localTimelineSettings.defaultToEndDate ? 'endDate' : 'duration'}
                              onValueChange={(value: string) => 
                                setLocalTimelineSettings(prev => ({ ...prev, defaultToEndDate: value === 'endDate' }))
                              }
                            >
                              <SelectTrigger id="default-mode" className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="endDate">End Date</SelectItem>
                                <SelectItem value="duration">Duration</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                              Whether to default to entering an End Date or a Duration
                            </p>
                          </div>
                        </div>

                        {/* Allow Mode Switch */}
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label htmlFor="allow-mode-switch" className="text-sm font-medium">
                              Allow Mode Switching
                            </Label>
                            <p className="text-xs text-muted-foreground">
                              When disabled, users cannot switch between End Date and Duration modes
                            </p>
                          </div>
                          <Switch
                            id="allow-mode-switch"
                            checked={localTimelineSettings.allowModeSwitch}
                            onCheckedChange={(checked) => 
                              setLocalTimelineSettings(prev => ({ ...prev, allowModeSwitch: checked }))
                            }
                          />
                        </div>
                      </div>
                    )}

                    {/* Field List */}
                    {groupedFields[section]
                      .sort((a, b) => a.order - b.order)
                      .map(field => (
                        <div key={field.key}>
                          <div
                            className="flex items-center justify-between p-4 border-b last:border-b-0 hover:bg-muted/30 transition-colors"
                          >
                            <span className="font-medium">{field.label}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">
                                {field.isMandatory ? 'Required' : 'Optional'}
                              </span>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div>
                                    <Switch
                                      checked={field.isMandatory}
                                      onCheckedChange={(checked) => handleToggle(field.key, checked)}
                                      disabled={field.isAlwaysMandatory}
                                    />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>
                                    {field.isAlwaysMandatory
                                      ? 'This field is always required'
                                      : field.isMandatory
                                      ? 'Required'
                                      : 'Optional'}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </div>
                          
                          {/* Additional settings for Project Code */}
                          {field.key === 'projectCode' && (
                            <div className="px-4 py-3 bg-muted/20 border-b last:border-b-0">
                              <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                  <Label htmlFor="auto-generate-code" className="text-sm font-medium">
                                    Automatic Code Creation
                                  </Label>
                                  <p className="text-xs text-muted-foreground">
                                    Auto-generate project code from project name
                                  </p>
                                </div>
                                <Switch
                                  id="auto-generate-code"
                                  checked={field.autoGenerateCode || false}
                                  onCheckedChange={(checked) => {
                                    setLocalConfigs(prev =>
                                      prev.map(config =>
                                        config.key === 'projectCode'
                                          ? { ...config, autoGenerateCode: checked }
                                          : config
                                      )
                                    );
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>
      </div>

      {/* Sticky Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <div className="max-w-3xl mx-auto flex justify-end items-center gap-3 p-4">
          <Button
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Save Changes
          </Button>
        </div>
      </div>
      </div>
    </TooltipProvider>
  );
}
